import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Seasons.css";

interface SeasonItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Seasons: React.FC = () => {
  const [data, setData] = useState<SeasonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/seasons`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching seasons:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: SeasonItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: SeasonItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Seasons",
    de: "Jahreszeiten",
    ta: "பருவங்கள்",
    si: "ඍතු",
    hi: "मौसम",
    ar: "فصول السنة",
    tr: "Mevsimler",
    ru: "Времена года",
    zh: "季节",
    fr: "Saisons",
    es: "Estaciones",
  };

  const subtitleMap = {
    en: "Seasons in German",
    de: "Jahreszeiten auf Deutsch",
    ta: "ஜெர்மன் மொழியில் பருவங்கள்",
    si: "ජර්මන් භාෂාවේ ඍතු",
    hi: "जर्मन में मौसम",
    ar: "فصول السنة بالألمانية",
    tr: "Almanca'da Mevsimler",
    ru: "Времена года на немецком",
    zh: "德语中的季节",
    fr: "Saisons en allemand",
    es: "Estaciones en alemán",
  };

  return (
    <div className="seasons-page-container">
      <div className="seasons-page-header">
        <div>
          <h1 className="seasons-page-title">{titleMap["de"]}</h1>
          <p className="seasons-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="seasons-page-loading">Loading...</div>
      ) : (
        <div className="seasons-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`seasons-page-card ${
                  isPlaying ? "seasons-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="seasons-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="seasons-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="seasons-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Seasons;
